import { CreateScheduledQueryBody, UpdateScheduledQueryBody } from '@api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_ALL_CONVERSATIONS } from '@terminal/views/alfred/research/hooks/useFetchInfiniteConversation';
import {
  createScheduledQuery,
  deleteScheduledQuery,
  runScheduledQueryNow,
  updateScheduledQuery,
} from '@terminal/views/alfred/research/services/scheduled-query-service';
import {
  QUERY_KEY_SCHEDULED_QUERIES,
  QUERY_KEY_SCHEDULED_QUERY,
  QUERY_KEY_SCHEDULED_QUERY_CONVERSATIONS,
} from './useScheduledQueries';

export const useCreateScheduledQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateScheduledQueryBody) => createScheduledQuery(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERIES],
      });
    },
  });
};

export const useUpdateScheduledQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateScheduledQueryBody;
    }) => updateScheduledQuery(id, body),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERIES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERY, id],
      });
    },
  });
};

export const useDeleteScheduledQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteScheduledQuery(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERIES],
      });
      queryClient.removeQueries({
        queryKey: [QUERY_KEY_SCHEDULED_QUERY, id],
      });
    },
  });
};

const invalidateAfter = (queryClient: ReturnType<typeof useQueryClient>, keys: unknown[][], delayMs: number) => {
  setTimeout(() => {
    keys.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
  }, delayMs);
};

export const useRunScheduledQueryNow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => runScheduledQueryNow(id),
    onSuccess: (_data, id) => {
      const keys = [
        [QUERY_ALL_CONVERSATIONS],
        [QUERY_KEY_SCHEDULED_QUERY_CONVERSATIONS, id],
      ];

      // Immediate invalidation — optimistic attempt in case BE is fast
      keys.forEach(key => queryClient.invalidateQueries({ queryKey: key }));

      // Delayed refetches to catch async conversation creation on the BE
      invalidateAfter(queryClient, keys, 3_000);
      invalidateAfter(queryClient, keys, 10_000);
    },
  });
};
